import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef, useMemo } from 'react';
import { useStyles } from '../../hooks/useStyles';
import { useImageLoaded } from '../../hooks/useImageLoaded';
import { hslColorFromString } from '../../helpers/colors/hslColorFromString';
import { createPolymorphicComponent } from '../../helpers/react/polymorphicComponentTypes';
import { Base } from '../Base';
import { avatarStyles, avatarDynamicStyles } from './Avatar.styles';
import { avatarTheme } from './Avatar.stylex';
import { avatarVariantStyles } from './variants';
export const Avatar = createPolymorphicComponent(forwardRef(function Avatar(props, forwardedRef) {
    const { styles, sx, alt, crossOrigin, referrerPolicy, src, srcSet, sizes, children, fallbackToRandomColor, randomColorSourceString: randomColorSourceStringProp, variant = 'rounded', ...other } = props;
    const variantStyles = avatarVariantStyles[variant];
    const { combineStyles, getStyles, globalStyles } = useStyles({
        name: 'Avatar',
        styles: [avatarStyles, variantStyles, styles],
    });
    // Use a hook instead of onError on the img element to support server-side
    // rendering.
    const { hasLoadingError } = useImageLoaded({
        crossOrigin,
        referrerPolicy,
        src,
        srcSet,
    });
    const hasImage = !!src || !!srcSet;
    const hasImageNotFailing = hasImage && !hasLoadingError;
    const randomColorSourceString = randomColorSourceStringProp ??
        (typeof children === 'string' ? children : undefined) ??
        alt ??
        src;
    const randomColor = useMemo(() => fallbackToRandomColor && randomColorSourceString
        ? hslColorFromString(randomColorSourceString)
        : undefined, [fallbackToRandomColor, randomColorSourceString]);
    return (_jsx(Base, { ...other, sx: [
            avatarTheme,
            globalStyles,
            combineStyles('host'),
            randomColor
                ? avatarDynamicStyles.backgroundColor(randomColor)
                : undefined,
            sx,
        ], ref: forwardedRef, children: hasImageNotFailing ? (_jsx("img", { ...getStyles('image'), alt: alt, crossOrigin: crossOrigin, referrerPolicy: referrerPolicy, src: src, srcSet: srcSet, sizes: sizes })) : children ? (_jsx("div", { ...getStyles('content'), children: children })) : hasImage && !!alt ? (_jsx("div", { ...getStyles('content'), children: alt[0] })) : null }));
}));
